import { useEffect, useRef, useState } from 'react';
import type { ReviewScan, ScanSnapshot } from './ReviewScan';

/**
 * The controller's snapshot as React state, through a selector so a consumer
 * re-renders only when the part it reads changes. The controller emits at most
 * once per frame while playing; without the selector every subscriber would
 * re-render on each of those. Without a scan the value is the selector's view
 * of nothing: null.
 */
export function useScanSnapshot<T = ScanSnapshot>(
  scan: ReviewScan,
  select?: (s: ScanSnapshot) => T,
  isEqual?: (a: T, b: T) => boolean,
): T;
export function useScanSnapshot<T = ScanSnapshot>(
  scan: ReviewScan | null,
  select?: (s: ScanSnapshot) => T,
  isEqual?: (a: T, b: T) => boolean,
): T | null;
export function useScanSnapshot<T = ScanSnapshot>(
  scan: ReviewScan | null,
  select?: (s: ScanSnapshot) => T,
  isEqual?: (a: T, b: T) => boolean,
): T | null {
  const pick = select ?? ((s: ScanSnapshot) => s as unknown as T);
  const same = isEqual ?? Object.is;
  const pickRef = useRef(pick);
  pickRef.current = pick;
  const sameRef = useRef(same);
  sameRef.current = same;
  const [value, setValue] = useState<T | null>(() => (scan ? pick(scan.snapshot()) : null));
  useEffect(() => {
    if (!scan) {
      setValue(null);
      return;
    }
    // subscribe calls back at once, which also covers an emission that landed
    // between the render and this effect.
    return scan.subscribe((s) => {
      const next = pickRef.current(s);
      setValue((prev) => (prev !== null && sameRef.current(prev, next) ? prev : next));
    });
  }, [scan]);
  return value;
}
